import Player from "@vimeo/player";
import { runInAction } from "mobx";
import WaveSurfer from "wavesurfer.js";
import YTPlayer from "yt-player";
import { heroState } from "./event-hero";

/** Wrapper to normalize 3rd party players  */
export default class JumboPlayer {
  constructor({ type, id, isBackground = null, playerStatus = null }) {
    this.type = type;
    this.id = id;
    this.isBackground = isBackground;
    this.wrapper = document.getElementById("combined-media-wrapper");
    this.playerStatus = playerStatus;
  }

  init = () => {
    const { type, id, isBackground, wrapper } = this;

    wrapper.innerHTML = "";
    wrapper.appendChild(DynamicMediaWrapper(type, id));

    const playerEl = wrapper.querySelector("#player");

    switch (type) {
      case "vimeo":
        //! no autoplay param here: url-param autoplay makes vimeo render its
        //! own "Unmute" overlay when the browser forces a muted start, and
        //! we'd have dueling chips. We drive playback via the api instead
        this.player = new Player(playerEl, {
          id: id,
          background: isBackground,
        });
        this.player
          .setColor(WP.branding.focus)
          .catch((error) => console.warn("Couldn't use brand color:", error));
        this.player.on("playing", () => {
          this.playerStatus = "playing";
          this.checkForMutedAutoplay();
        });
        this.player.on("pause", () => {
          this.playerStatus = "pause";
        });
        this.player.on("ended", () => {
          this.playerStatus = "ended";
          if (typeof id === "string" && id.includes("event")) {
            runInAction(() => {
              heroState.resetLiveStream = true;
            });
          }
        });
        break;
      case "youtube":
        this.player = new YTPlayer(playerEl, {
          modestBranding: true,
        });
        this.player.load(id, { autoplay: true });
        break;
      case "ivs":
        registerIVSTech(videojs);
        registerIVSQualityPlugin(videojs);
        this.player = videojs(playerEl, {
          techOrder: ["AmazonIVS"],
          controls: true,
          autoplay: true,
          playsInline: true,
          controlBar: {
            playToggle: { replay: false },
            pictureInPictureToggle: false,
          },
        });
        const readyCallback = () => {
          this.player.enableIVSQualityPlugin();
          this.player.src(id);
          requestAnimationFrame(() => this.player.play());
        };
        this.player.ready(readyCallback);
        break;
      case "cloudflare":
        this.player = videojs(playerEl, {
          controls: true,
          autoplay: true,
          playsInline: true,
          controlBar: {
            playToggle: { replay: false },
            pictureInPictureToggle: false,
          },
        });
        this.player.src({
          src: id,
          type: "application/x-mpegURL",
        });
        this.player.on("error", (e) => {
          playerEl.classList.add("vjs-waiting");
        });
        this.player.on("ended", () => {
          runInAction(() => {
            heroState.resetLiveStream = true;
          });
        });
        break;
      case "wavesurfer":
        this.player = WaveSurfer.create({
          container: playerEl,
          scrollParent: false,
          height: 180,
          cursorColor: "transparent",
          waveColor: WP.audioPlayer.barColor,
          progressColor: WP.audioPlayer.waveProgressColor,
          responsive: true,
          backend: "MediaElementWebAudio",
        });
        this.player.load(id);
        // ? wavesurfer needs some event listeners as well
        (() => {
          const playPauseBtn = document.querySelector(".js-play-pause");
          playPauseBtn.addEventListener("click", () => this.player.playPause());
          this.player.on("pause", () => {
            playPauseBtn.classList.remove("state-play");
            playPauseBtn.classList.add("state-pause");
          });
          this.player.on("play", () => {
            playPauseBtn.classList.remove("state-pause");
            playPauseBtn.classList.add("state-play");
          });
        })();
        break;
    }

    if (isBackground) this.mute();
    else this.unmute();

    if (this.type !== "ivs") this.play(); //? IVS play is triggered in its "ready callback" above
  };

  play = () => {
    this.player?.play()?.catch((error) => {
      //? a pending play() interrupted by teardown or loadVideo() is benign
      if (error.name === "PlayInterrupted") return;

      //? the browser's autoplay policy blocked an unmuted programmatic play
      //? (safari, notably): fall back to muted playback + "tap for sound" chip
      if (
        error.name === "NotAllowedError" &&
        this.type === "vimeo" &&
        !this.isBackground
      ) {
        this.mute();
        this.player
          .play()
          .then(() => this.showUnmuteChip())
          .catch((error) => console.warn("Playback error:", error));
        return;
      }

      console.warn("Playback error:", error);
    });
  };

  //? when the browser blocks unmuted autoplay, vimeo usually rescues it
  //? internally: it sets its own muted flag and plays anyway, and the sdk's
  //? play() promise resolves (no NotAllowedError for us to catch). So on the
  //? first "playing" we ask the player whether it's actually audible, and
  //? offer the "tap for sound" chip if not
  checkForMutedAutoplay = () => {
    if (this.checkedForMutedAutoplay) return; //? first playing only
    this.checkedForMutedAutoplay = true;

    if (this.isBackground) return;

    //! never unmute programmatically here: webkit reacts to an api unmute of
    //! a policy-muted playing video by PAUSING it. The chip click (a real
    //! gesture) is the only reliable unmute
    Promise.all([this.player.getMuted(), this.player.getVolume()]).then(
      ([muted, volume]) => {
        if (muted || volume === 0) this.showUnmuteChip();
        //? audible: clear any stale chip left over from a previous muted clip
        else this.hideUnmuteChip();
      }
    );
  };

  pause = () => {
    this.player?.pause();
  };

  //? shown when autoplay policy forced us into muted playback (see play());
  //? lives inside the wrapper, so any re-init clears it automatically
  showUnmuteChip = () => {
    if (this.wrapper.querySelector(".hero-unmute-chip")) return;

    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "hero-unmute-chip";
    chip.innerHTML = `
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6z"/>
      </svg>
      Tap for sound
    `;
    chip.addEventListener("click", () => {
      this.unmute();
      //? remove directly: vimeo doesn't reliably fire volumechange for a
      //? muted-flag flip, only for volume changes
      this.hideUnmuteChip();
    });
    this.wrapper.appendChild(chip);

    //? sound from the player's own volume control also removes the chip
    this.player.on("volumechange", this.hideUnmuteChipOnSound);
  };

  hideUnmuteChip = () => {
    this.wrapper.querySelector(".hero-unmute-chip")?.remove();
    this.player.off("volumechange", this.hideUnmuteChipOnSound);
  };

  hideUnmuteChipOnSound = ({ volume }) => {
    if (volume > 0) this.hideUnmuteChip();
  };

  destroy = () => {
    if (this.type === "ivs" || this.type === "cloudflare") {
      this.player?.dispose();
    } else {
      this.player?.destroy();
    }
  };

  mute = () => {
    if (this.type === "ivs" || this.type === "cloudflare") {
      this.player?.volume(0);
    } else {
      this.player?.setVolume(0);
    }
  };

  //? used in clip stat tracking (vimeo, youtube, and wavesurfer, so far...)
  getCurrentTime = () => this.player?.getCurrentTime();

  getPlayerStatus = () => {
    switch (this.type) {
      case "vimeo":
        return this.playerStatus;
      case "youtube":
        return this.player.getState();
      case "wavesurfer":
        return this.player.isPlaying() ? "playing" : null;
      default:
        //? not implemented for other types (yet?)
        return null;
    }
  };

  on = (e, fn) => this.player?.on(e, fn);

  off = (e, fn) => {
    switch (this.type) {
      case "vimeo":
        this.player.off(e, fn);
        break;

      case "wavesurfer":
        this.player.un(e, fn);
        break;
      default:
        null;
        break;
    }
  };

  unmute = () => {
    switch (this.type) {
      case "vimeo":
        //? setVolume alone doesn't clear vimeo's internal muted flag
        //? (set by its own muted-autoplay fallback), so clear both
        this.player?.setMuted(false);
        this.player?.setVolume(1);
        break;
      case "wavesurfer":
        this.player?.setVolume(1);
        break;
      case "ivs":
      case "cloudflare":
        this.player?.volume(1);
        break;
      case "youtube":
        this.player?.setVolume(100);
        break;
      default:
        null;
        break;
    }
  };

  updateMedia = (id) => {
    this.id = id;
    this.checkedForMutedAutoplay = false; //? re-check on the new media

    //? only needs to support clip types, since those are the only cases where
    //? we would switch from one media to another of the same type back to back
    switch (this.type) {
      case "vimeo":
        //? load into the existing player: it keeps its unmuted state, so in
        //? browsers with media engagement the next clip keeps its sound
        this.player
          .loadVideo(id)
          .then(() => {
            this.play();

            //? webkit can refuse an unmuted play() SILENTLY (no rejection,
            //? no rescue: the player just sits paused). If nothing is
            //? playing shortly after, and the user didn't pause it
            //? themselves, recover into muted playback (+ chip)
            setTimeout(() => {
              this.player.getPaused().then((paused) => {
                if (paused && this.playerStatus !== "pause") {
                  this.mute();
                  this.play();
                }
              });
            }, 1000);
          })
          .catch((error) => console.warn("Couldn't load video:", error));
        break;
      case "wavesurfer":
        this.player.load(id);
        requestAnimationFrame(() => this.play());
        break;
      case "youtube":
        this.player.load(id, { autoplay: true });
        break;
      case "image":
      case "pdf":
        this.wrapper.innerHTML = "";
        this.wrapper.appendChild(DynamicMediaWrapper(this.type, id));
        break;
    }
  };
}

function DynamicMediaWrapper(type, id) {
  const wrapper = document.createElement("div");
  let htmlTag = "div",
    className,
    style,
    extras;

  switch (type) {
    case "wavesurfer":
      className = "hero-media-audio";
      extras = `
        <a class="js-play-pause play-button state-pause">
          <i class="fa fa-play"></i>
          <i class="fa fa-pause"></i>
        </a>
      `;
      break;
    case "pdf":
      className = "hero-media-pdf";
      extras = `
        <iframe
          id="pdf-viewer"
          src="${id}"
          width="580"
          height="326"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen=""
          style="height: 100%; width: 100%;"
        ></iframe>
      `;
      break;

    case "image":
      className = "hero-media-image";
      extras = `<img id="hero-image" src="${id}">`;
      break;

    case "ivs":
    case "cloudflare":
      className = "video-js vjs-big-play-centered";
      htmlTag = "video";
      style = { padding: "0 0 56.25%", width: "100%" };
      break;

    case "zoom":
      className = "hero-media-video";
      extras = `
        <iframe
          id="zoom-player"
          frameborder="0"
          scrolling="no"
          sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
          allow="microphone; camera; fullscreen;"
          src="${id}"
        ></iframe>
      `;
      break;

    case "jitsi":
      className = "hero-media-video";
      extras = `
        <iframe
          id="jitsi-player"
          frameborder="0"
          scrolling="no"
          allow="microphone; camera; fullscreen; display-capture"
          src="${id}"
        >
        </iframe>
      `;
      break;

    case "rally":
      className = "hero-media-video";
      extras = `
        <iframe
          id="rally-player"
          frameborder="0"
          scrolling="yes"
          allow="microphone; camera; fullscreen; display-capture"
          src="${id}"
        >
        </iframe>
      `;
      break;

    case "royalslider":
      className = "hero-media-slider";
      extras = `
        <iframe
          id="royalslider-player"
          frameborder="0"
          scrolling="no"
          allow="microphone; camera; fullscreen; display-capture"
          src="${id}"
        >
        </iframe>
      `;
      break;

    default:
      className = "hero-media-video";
      break;
  }

  wrapper.className = className;
  if (style) Object.assign(wrapper.style, style);
  wrapper.innerHTML = `
    ${extras ? extras : ""}
    <${htmlTag} id="player"></${htmlTag}>
  `;

  return wrapper;
}
